import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const CARDS = [
  {
    id: "ice-cream-social",
    title: "Ice Cream Social Experience",
    desc: "A nostalgic gathering filled with conversation, laughter, and candid memories.",
    image: "https://images.pexels.com/photos/34210610/pexels-photo-34210610.jpeg",
    alt: "Older adults enjoying a warm, welcoming social gathering",
  },
  {
    id: "legacy-portrait-story-day",
    title: "Legacy Portrait & Story Day",
    desc: "A dignified experience celebrating residents' lives, relationships, and personal stories.",
    image: "https://images.unsplash.com/photo-1662987619545-1844207dedac",
    alt: "A dignified portrait celebrating a resident's life and personal story",
  },
  {
    id: "seasonal-family-experience",
    title: "Seasonal Family Experience",
    desc: "A flexible gathering for Grandparents Day, holidays, resident appreciation, or another meaningful occasion.",
    image: "https://images.unsplash.com/photo-1655070748916-75871ab03c87",
    alt: "Multigenerational family members connecting with a resident",
  },
];

export default function MoreWays({ onSchedule }) {
  return (
    <section id="more-ways" className="py-24 md:py-32 bg-cream">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14 md:mb-20">
          <Reveal>
            <Eyebrow>More Experiences</Eyebrow>
            <h2 className="mt-5 font-poppins font-bold text-charcoal text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
              More ways to bring your community together.
            </h2>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-charcoal/70">
              Every community is different. These experiences can be thoughtfully customized around your
              residents, families, and calendar.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`more-ways-card-${c.id}`}
              className="group flex flex-col rounded-[2rem] bg-white border border-border overflow-hidden transition-all hover:-translate-y-2 hover:shadow-[0_36px_70px_-30px_rgba(73,53,72,0.28)]"
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={c.image}
                  alt={c.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col flex-1 p-7">
                <h3 className="font-poppins font-bold text-xl md:text-2xl text-charcoal leading-snug">{c.title}</h3>
                <p className="mt-3 text-charcoal/70 leading-relaxed flex-1">{c.desc}</p>
                <button
                  data-testid={`more-ways-cta-${c.id}`}
                  onClick={() => onSchedule(`I'm interested in learning more about the ${c.title}.`)}
                  className="mt-6 inline-flex items-center gap-1.5 font-semibold text-coral group/btn"
                >
                  Ask About This Experience
                  <ArrowRight size={18} className="transition-transform group-hover/btn:translate-x-0.5" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p data-testid="more-ways-clarification" className="mt-10 md:mt-12 max-w-3xl text-sm md:text-base leading-relaxed text-charcoal/55">
            Customized experiences are quoted individually. Venue, refreshments, décor, and special materials
            are provided by the community unless included in a custom proposal.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
